import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { WorkspaceIconsModule } from '../../shared/workspace-icons.module';
import { EMPTY, Subject, expand, map, of, take, takeUntil, toArray } from 'rxjs';
import { CacheService } from '../../services/cache.service';
import { FileItem, FilesService } from './files.service';
import { FileDropTargetDirective } from './file-drop-target.directive';

interface FolderNode {
  id: string | null; area: string; name: string;
  expanded: boolean; children: FolderNode[]; page: number; more: boolean; loading: boolean; error: boolean;
}
export interface FileLocation { area: string; folder: string | null; }

/** Directory navigation and drop locations; the page owns file mutations. */
@Component({
  selector: 'app-file-tree', standalone: true, imports: [CommonModule, WorkspaceIconsModule, FileDropTargetDirective],
  templateUrl: './file-tree.component.html', styleUrls: ['./file-tree.component.css'],
})
export class FileTreeComponent implements OnChanges, OnDestroy {
  @Input() area = 'personal';
  @Input() folder: FileItem | null = null;
  @Input() revision = 0;
  @Input() canDropFile: (location: FileLocation) => boolean = () => false;
  @Output() fileDropped = new EventEmitter<FileLocation>();
  @Output() locationChange = new EventEmitter<FileLocation>();
  /** The selected folder's ancestors and itself, once resolved. */
  @Output() pathResolved = new EventEmitter<FileItem[]>();
  roots: FolderNode[] = [];
  pathError = false;
  folderPath: FileItem[] = [];
  private revealedPath = new Set<string>();
  private reset$ = new Subject<void>();
  private pathChanged$ = new Subject<void>();
  private destroyed$ = new Subject<void>();
  constructor(private files: FilesService, cache: CacheService) {
    cache.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.folder = null; this.reset();
    });
  }
  private node(area: string, name: string, id: string | null = null): FolderNode {
    return { id, area, name, expanded: false, children: [], page: 0, more: false, loading: false, error: false };
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.roots.length || changes['revision']) this.reset();
    if (this.folder) this.reveal();
    else {
      this.pathChanged$.next(); this.pathError = false; this.folderPath = []; this.revealedPath.clear();
      const root = this.roots.find(node => node.area === this.area);
      if (root) this.open(root);
    }
  }
  private reset(): void {
    this.reset$.next(); this.pathChanged$.next(); this.pathError = false; this.folderPath = []; this.revealedPath.clear();
    this.roots = [this.node('personal', 'My Files'), this.node('shared', 'Shared Files')];
  }
  selected(node: FolderNode): boolean {
    return node.area === this.area && node.id === (this.folder?.id ?? null);
  }
  choose(node: FolderNode): void {
    this.open(node);
    this.locationChange.emit({ area: node.area, folder: node.id });
  }
  toggle(node: FolderNode): void {
    if (node.expanded) node.expanded = false;
    else this.open(node);
  }
  private open(node: FolderNode): void {
    node.expanded = true;
    if (!node.page && !node.loading) this.load(node);
  }
  load(node: FolderNode): void {
    if (node.loading) return;
    node.loading = true; node.error = false;
    const page = node.page + 1;
    this.files.list({ search: '', filters: {}, page, pageSize: 100 }, node.area, node.id)
      .pipe(takeUntil(this.reset$), takeUntil(this.destroyed$)).subscribe({
        next: result => {
          const folders = result.items.filter(file => file.isFolder);
          const existing = new Map(node.children.map(child => [child.id, child]));
          const children = folders.map(file => {
            const child = existing.get(file.id) ?? this.node(node.area, file.name, file.id);
            child.name = file.name; return child;
          });
          node.children = page === 1
            ? [...children, ...node.children.filter(child => child.id && this.revealedPath.has(child.id) && !folders.some(file => file.id === child.id))]
            : [...node.children, ...children.filter(child => !existing.has(child.id))];
          node.page = page; node.loading = false;
          // FileCatalogRepository orders folders before files. Stop as soon as this page reaches files.
          node.more = result.items.length > 0 && folders.length === result.items.length && page * 100 < result.total;
        },
        error: () => { node.loading = false; node.error = true; },
      });
  }
  reveal(): void {
    this.pathChanged$.next(); this.pathError = false; this.folderPath = [];
    if (!this.folder) return;
    of(this.folder).pipe(
      expand(file => file.parentId ? this.files.get(file.parentId).pipe(map(detail => detail.file)) : EMPTY),
      take(64), toArray(), takeUntil(this.pathChanged$), takeUntil(this.reset$), takeUntil(this.destroyed$),
    ).subscribe({
      next: ancestors => {
        this.folderPath = ancestors.reverse();
        this.pathResolved.emit(this.folderPath);
        this.revealedPath = new Set(ancestors.map(file => file.id));
        const root = this.roots.find(node => node.area === (this.folder?.isShared ? 'shared' : 'personal'));
        if (!root) return;
        let parent: FolderNode = root;
        for (const file of this.folderPath) {
          this.open(parent);
          let child: FolderNode | undefined = parent.children.find(node => node.id === file.id);
          if (!child) { child = this.node(parent.area, file.name, file.id); parent.children.push(child); }
          child.name = file.name; parent = child;
        }
        this.open(parent);
      },
      error: () => { this.pathError = true; },
    });
  }
  ngOnDestroy(): void {
    this.destroyed$.next(); this.destroyed$.complete(); this.reset$.complete(); this.pathChanged$.complete();
  }
}
